/**
 * Theme Hook
 *
 * WHY: Detects current theme (dark/light) based on the document's class.
 * The app uses Tailwind's class-based dark mode strategy.
 */

import { useState, useEffect } from 'react'

export type Theme = 'dark' | 'light'

export function useTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
    }
    return 'dark' // Default to dark
  })

  useEffect(() => {
    // Create a MutationObserver to watch for class changes on the html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark')
          setTheme(isDark ? 'dark' : 'light')
        }
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  return theme
}

/**
 * Toggle theme helper
 * Call this to switch between dark and light mode
 */
export function toggleTheme(): void {
  const html = document.documentElement
  if (html.classList.contains('dark')) {
    html.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  } else {
    html.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  }
}

/**
 * Initialize theme from localStorage or system preference
 * Call this on app startup
 */
export function initializeTheme(): void {
  const stored = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  if (stored === 'light') {
    document.documentElement.classList.remove('dark')
  } else if (stored === 'dark' || prefersDark) {
    document.documentElement.classList.add('dark')
  }
}
